import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const TableNfcCodes = () => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin';
  const [restaurant, setRestaurant] = useState(null);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [r, t] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/restaurants/${restaurantId}`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/tables/restaurant/${restaurantId}`, { withCredentials: true }),
        ]);
        setRestaurant(r.data);
        setTables(t.data.sort((a, b) => a.number - b.number));
      } catch (e) {
        setError(e.response?.data?.detail || 'Yüklənmə xətası');
      } finally {
        setLoading(false);
      }
    })();
  }, [restaurantId]);

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {}
  };

  const copyAllUrls = async () => {
    const txt = tables.map((t) => `Masa ${t.number}: ${t.qr_url}`).join('\n');
    await navigator.clipboard.writeText(txt);
    setCopiedId('all');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const downloadCsv = () => {
    const header = 'Masa Nomresi,Ad,NFC URL\n';
    const rows = tables.map((t) => `${t.number},"${t.name || 'Masa ' + t.number}",${t.qr_url}`).join('\n');
    const blob = new Blob(['\ufeff' + header + rows], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nfc-urls-${restaurant?.name || restaurantId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadJson = () => {
    const data = tables.map((t) => ({
      table_number: t.number,
      name: t.name || `Masa ${t.number}`,
      url: t.qr_url,
    }));
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nfc-urls-${restaurant?.name || restaurantId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="nfc-codes-page">
      {/* Header */}
      <div className="bg-white border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => navigate(`/restaurant/${restaurantId}/tables`)} className="text-stone-600 hover:text-emerald-600 flex items-center gap-1" data-testid="nfc-back-btn">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
              <span className="hidden sm:inline">Masalar</span>
            </button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-stone-900">📡 NFC Kodları</h1>
              <p className="text-xs text-stone-500 truncate">{restaurant?.name}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {error && (
          <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
        )}

        {/* Explainer */}
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center text-2xl shrink-0">📡</div>
            <div>
              <h2 className="text-lg font-bold text-stone-900 mb-1">NFC nədir və necə işləyir?</h2>
              {isAdmin ? (
                <p className="text-sm text-stone-700 leading-relaxed">
                  NFC — kiçik yapışqan stiker (təxminən 2.5 sm) olub içində çip var. Müştəri telefonu stikerə yaxınlaşdırdıqda menyu <strong>avtomatik açılır</strong> — QR skan etməyə ehtiyac yoxdur.
                  Aşağıdakı hər masa üçün fərdi URL var — bu URL-ləri NFC stikerlərinizə yazın.
                </p>
              ) : (
                <p className="text-sm text-stone-700 leading-relaxed">
                  NFC ilə müştəriləriniz telefonu masaya yaxınlaşdırdıqda menyunuz <strong>avtomatik açılır</strong> — QR skan etməyə ehtiyac yoxdur. Müasir və sürətli təcrübə.
                  <br /><br />
                  <strong>Sifariş etmək istəyirsiniz?</strong> Bizə <a href="mailto:info@smartmenyu.az" className="text-emerald-700 underline">info@smartmenyu.az</a> ünvanından yazın — restoranınıza uyğun paket təklif edək.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Bulk actions — admin only (used for NFC programming) */}
        {isAdmin && (
        <div className="bg-white border border-stone-200 rounded-2xl p-5">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div>
              <h3 className="font-bold text-stone-900">Toplu ixrac</h3>
              <p className="text-xs text-stone-500 mt-0.5">{tables.length} masa üçün bütün URL-lər</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={copyAllUrls} className="px-4 py-2 bg-stone-100 hover:bg-stone-200 text-stone-800 text-sm rounded-lg font-medium" data-testid="nfc-copy-all">
                {copiedId === 'all' ? '✅ Kopyalandı' : '📋 Hamısını kopyala'}
              </button>
              <button onClick={downloadCsv} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-lg font-medium" data-testid="nfc-download-csv">
                📄 CSV yüklə
              </button>
              <button onClick={downloadJson} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-lg font-medium" data-testid="nfc-download-json">
                📦 JSON yüklə
              </button>
            </div>
          </div>
        </div>
        )}

        {/* Tables list — admin only (URLs must not leak to restaurant owners) */}
        {isAdmin && (tables.length === 0 ? (
          <div className="bg-white border border-stone-200 rounded-2xl p-10 text-center text-stone-500">
            Hələ masa yaratmamısınız. <Link to={`/restaurant/${restaurantId}/tables`} className="text-emerald-600 hover:underline font-semibold">Masalar səhifəsində</Link> masa əlavə edin.
          </div>
        ) : (
          <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
            <div className="px-5 py-3 border-b border-stone-100 bg-stone-50">
              <h3 className="font-bold text-stone-900">Masa URL-ləri ({tables.length})</h3>
            </div>
            <div className="divide-y divide-stone-100">
              {tables.map((t) => (
                <div key={t.id} className="flex flex-wrap items-center gap-3 p-4 hover:bg-stone-50" data-testid={`nfc-table-${t.number}`}>
                  <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-800 font-bold flex items-center justify-center shrink-0">
                    {t.number}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-stone-900">Masa {t.number}{t.name && t.name !== `Masa ${t.number}` ? ` — ${t.name}` : ''}</div>
                    <div className="text-xs text-stone-500 font-mono break-all mt-0.5">{t.qr_url}</div>
                  </div>
                  <button
                    onClick={() => copyToClipboard(t.qr_url, t.id)}
                    className="px-3 py-1.5 bg-stone-100 hover:bg-emerald-100 hover:text-emerald-800 text-stone-700 text-xs rounded-lg font-medium"
                    data-testid={`nfc-copy-${t.number}`}
                  >
                    {copiedId === t.id ? '✅ Kopyalandı' : '📋 Kopyala'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Step-by-step guide — admin only */}
        {isAdmin && (
        <div className="bg-white border border-stone-200 rounded-2xl p-5 space-y-4">
          <h3 className="text-lg font-bold text-stone-900">📋 Necə proqramlaşdırılır — Addım-addım</h3>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-stone-50 rounded-xl p-4">
              <div className="text-xs text-emerald-700 font-bold mb-1">ADDIM 1</div>
              <div className="font-semibold text-stone-900 mb-1.5">NFC stikerləri sifariş edin</div>
              <p className="text-sm text-stone-600 leading-relaxed">
                <strong>NTAG213</strong> növ stiker alın (144 bayt, su keçirməz). Metal masa üçün <strong>On-Metal NFC</strong> seçin.
              </p>
              <div className="mt-2 space-y-1 text-xs">
                <a href="https://www.aliexpress.com/w/wholesale-ntag213-sticker.html" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline block">→ AliExpress-də NTAG213</a>
                <a href="https://www.trendyol.com/sr?q=ntag213%20nfc%20etiket" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline block">→ Trendyol NFC etiketləri</a>
              </div>
            </div>

            <div className="bg-stone-50 rounded-xl p-4">
              <div className="text-xs text-emerald-700 font-bold mb-1">ADDIM 2</div>
              <div className="font-semibold text-stone-900 mb-1.5">"NFC Tools" tətbiqini yükləyin</div>
              <p className="text-sm text-stone-600 leading-relaxed">
                Pulsuz tətbiq. Stikerlərə URL yazmaq üçün istifadə edəcəksiniz.
              </p>
              <div className="mt-2 space-y-1 text-xs">
                <a href="https://play.google.com/store/apps/details?id=com.wakdev.wdnfc" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline block">→ Google Play (Android)</a>
                <a href="https://apps.apple.com/app/nfc-tools/id1252962749" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline block">→ App Store (iPhone)</a>
              </div>
            </div>

            <div className="bg-stone-50 rounded-xl p-4">
              <div className="text-xs text-emerald-700 font-bold mb-1">ADDIM 3</div>
              <div className="font-semibold text-stone-900 mb-1.5">URL-i stikerə yazın</div>
              <ol className="text-sm text-stone-600 leading-relaxed space-y-1 list-decimal ml-4">
                <li>NFC Tools açın → <strong>"Yazın"</strong> (Write)</li>
                <li><strong>"Kayıt ekle"</strong> → <strong>"URL"</strong> seçin</li>
                <li>Yuxarıdan masa URL-ini kopyalayıb yapışdırın</li>
                <li><strong>"Tamam"</strong> → <strong>"Yaz"</strong></li>
                <li>Telefonu boş stikerə yaxınlaşdırın</li>
                <li>✅ "Yazma başarılı" görünsə hazırdır</li>
              </ol>
            </div>

            <div className="bg-stone-50 rounded-xl p-4">
              <div className="text-xs text-emerald-700 font-bold mb-1">ADDIM 4</div>
              <div className="font-semibold text-stone-900 mb-1.5">Masaya yapışdırın və sınayın</div>
              <p className="text-sm text-stone-600 leading-relaxed">
                Stikeri masanın görünən yerinə (və ya QR kodun arxasına) yapışdırın. Başqa telefonla təmas edib menyunun avtomatik açıldığını yoxlayın. Hər masa üçün eyni prosedur.
              </p>
              <p className="text-xs text-stone-500 mt-2">
                💡 <strong>İpucu:</strong> Yanına kiçik "NFC 📱" işarəsi qoyun ki, müştərilər bilsin.
              </p>
            </div>
          </div>
        </div>
        )}

        {/* FAQ — admin only */}
        {isAdmin && (
        <div className="bg-white border border-stone-200 rounded-2xl p-5 space-y-3">
          <h3 className="text-lg font-bold text-stone-900">❓ Tez-tez soruşulan suallar</h3>
          <details className="text-sm">
            <summary className="cursor-pointer font-semibold text-stone-800 py-1.5">iPhone-larda NFC işləyir?</summary>
            <p className="text-stone-600 pl-4 pb-2">Bəli, iPhone XS və sonrakı modellərdə (iOS 14+) heç bir tətbiqə ehtiyac yoxdur. iPhone 7-XR arasında Notification Center-i açmaq lazım ola bilər.</p>
          </details>
          <details className="text-sm">
            <summary className="cursor-pointer font-semibold text-stone-800 py-1.5">Stikerlər neçə dəfə istifadə olunur?</summary>
            <p className="text-stone-600 pl-4 pb-2">Yüz minlərlə dəfə oxunur — praktik olaraq sonsuz. Yalnız fiziki zədə (qopma, xəttlər) problem yaradır.</p>
          </details>
          <details className="text-sm">
            <summary className="cursor-pointer font-semibold text-stone-800 py-1.5">Yenidən proqramlaşdırıla bilər?</summary>
            <p className="text-stone-600 pl-4 pb-2">Bəli, NTAG213 stikerləri yenidən yazıla bilər. Yeni URL yazsanız köhnəsi silinir.</p>
          </details>
          <details className="text-sm">
            <summary className="cursor-pointer font-semibold text-stone-800 py-1.5">Metal masada işləyirmi?</summary>
            <p className="text-stone-600 pl-4 pb-2">Standart NTAG213 metal səthdə işləmir. Metal üçün xüsusi <strong>"On-Metal NFC"</strong> stikeri alın — arxasında ferrit təbəqəsi var.</p>
          </details>
          <details className="text-sm">
            <summary className="cursor-pointer font-semibold text-stone-800 py-1.5">Toplu proqramlaşdırma mümkündür?</summary>
            <p className="text-stone-600 pl-4 pb-2">Hər stikeri fərdi olaraq təmas edib yazmaq lazımdır (30 saniyə/ədəd). CSV faylı yükləyib telefonu masa 1-dən başlayaraq sıra ilə yazın.</p>
          </details>
        </div>
        )}
      </div>
    </div>
  );
};

export default TableNfcCodes;
